// Home-page retention surface. Where the slim banner is a persistent
// reminder, this card is the salesperson — large, warm, with two
// loss-aversion stats and a one-click Resume CTA. Lives directly under
// the page header (above KPIs) so it's the first thing a returning
// tenant sees when their subscription is in trouble.
//
// Tone deliberately non-punitive: the customer didn't sign up to be
// scolded for clicking Cancel. The framing is "your work is preserved,
// come back" rather than "you broke something". Loss-aversion lands
// best when paired with continuity language.
//
// State C (past-due / frozen) routes to /billing instead of firing
// Resume — payment-method updates happen in the Stripe Customer Portal
// which the OpenPortalButton on /billing handles. Inlining the portal
// handoff would duplicate that logic and split the entry point.

import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Bot, MessagesSquare, RotateCcw, ShieldAlert, Sparkles } from 'lucide-react';
import type { UsageSummary } from '@cortex/shared';
import { ApiError } from '../../lib/api-client.js';
import { useResumeSubscription } from '../../lib/api/billing.js';
import { deriveSubscriptionState, type SubscriptionState } from '../../lib/subscription-state.js';
import { formatDate } from '../../lib/format-date.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { cn } from '../../lib/utils.js';

interface RetentionCardProps {
  summary: UsageSummary;
  agentCount: number | null;
}

export function RetentionCard({ summary, agentCount }: RetentionCardProps) {
  const state = deriveSubscriptionState(summary.plan);
  if (state === 'none') return null;

  const cancelDate = summary.plan.cancelAt ? formatDate(summary.plan.cancelAt) : null;
  // Two stats max — over-decorating dilutes the loss-aversion signal. Pick
  // whichever two are most concrete for this tenant's history.
  const stats = pickStats(summary, agentCount);

  return (
    <Card
      className={cn(
        'border-2',
        state === 'scheduled-cancel' ? 'border-warning/40' : 'border-destructive/40',
      )}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {state === 'scheduled-cancel' ? (
            <Sparkles className="h-5 w-5 text-warning" aria-hidden />
          ) : (
            <ShieldAlert
              className={cn(
                'h-5 w-5',
                state === 'past-due' ? 'text-destructive' : 'text-destructive',
              )}
              aria-hidden
            />
          )}
          {headlineFor(state)}
        </CardTitle>
        <CardDescription>{bodyFor(state, cancelDate)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats.length > 0 && (
          <ul className="grid gap-3 sm:grid-cols-2">
            {stats.map((s) => (
              <li
                key={s.label}
                className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2"
              >
                <s.icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm font-semibold tabular-nums">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
        <RetentionActions state={state} />
      </CardContent>
    </Card>
  );
}

function RetentionActions({ state }: { state: SubscriptionState }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resume = useResumeSubscription();

  // Past-due needs the Stripe Customer Portal — Resume can't fix a failed
  // payment. Send the user to /billing where the portal handoff already
  // exists rather than duplicating that flow inline.
  if (state === 'past-due') {
    return (
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="default">
          <Link to="/billing">Update payment method</Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/billing">View billing</Link>
        </Button>
      </div>
    );
  }

  const onResume = () => {
    setError(null);
    resume.mutate(undefined, {
      onSuccess: () => {
        setConfirming(false);
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setError('Stripe billing is not configured on this environment.');
            return;
          }
          if (err.status === 402 || err.status === 409) {
            setError('Subscription state changed. Refresh and try again.');
            return;
          }
        }
        setError(err instanceof Error ? err.message : 'Could not resume.');
      },
    });
  };

  if (!confirming) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => { setConfirming(true); }}
          variant="default"
          aria-label={state === 'canceled' ? 'Reactivate subscription' : 'Continue subscription'}
        >
          <RotateCcw aria-hidden />
          {state === 'canceled' ? 'Reactivate' : 'Continue subscription'}
        </Button>
        <Button asChild variant="ghost">
          <Link to="/billing">Manage subscription</Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      <Button onClick={onResume} disabled={resume.isPending} variant="default">
        {resume.isPending ? 'Resuming…' : 'Confirm'}
      </Button>
      <Button
        onClick={() => { setConfirming(false); }}
        disabled={resume.isPending}
        variant="ghost"
      >
        Cancel
      </Button>
      {error && (
        <p role="alert" className="w-full text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function headlineFor(state: SubscriptionState): string {
  if (state === 'scheduled-cancel') return "Don't miss a beat";
  if (state === 'canceled') return 'Welcome back — your agents are waiting';
  return 'Quick fix needed — update your payment';
}

function bodyFor(state: SubscriptionState, cancelDate: string | null): string {
  if (state === 'scheduled-cancel') {
    return cancelDate
      ? `Your subscription is scheduled to end on ${cancelDate}. After that, your tenant continues on the Free tier — agents, knowledge base, and conversation history all preserved. Resume to keep your current paid limits.`
      : 'Your subscription is scheduled to end. After that, your tenant continues on the Free tier — agents, knowledge base, and conversation history all preserved. Resume to keep your current paid limits.';
  }
  if (state === 'canceled') {
    return cancelDate
      ? `Your subscription ended on ${cancelDate}. Reactivate to instantly restore access — your agents, knowledge base, and conversation history are all preserved.`
      : 'Reactivate to instantly restore access — your agents, knowledge base, and conversation history are all preserved.';
  }
  return "We couldn't process your last payment. Update your card to keep your service running — your work is unaffected in the meantime.";
}

interface RetentionStat {
  label: string;
  value: string;
  icon: typeof Bot;
}

// Pick up to two concrete numbers that frame what the tenant has built.
// Agents (deployed assets) and conversations (engagement history) are the
// most universally meaningful. Falls back to "no stats" rather than padding
// with zero-state numbers — a "0 conversations preserved" reads as worse
// than no callout at all.
function pickStats(summary: UsageSummary, agentCount: number | null): RetentionStat[] {
  const stats: RetentionStat[] = [];
  if (agentCount !== null && agentCount > 0) {
    stats.push({
      label: agentCount === 1 ? 'agent ready' : 'agents ready',
      value: agentCount.toLocaleString(),
      icon: Bot,
    });
  }
  if (summary.period.messages > 0) {
    stats.push({
      label:
        summary.period.messages === 1 ? 'conversation this period' : 'conversations this period',
      value: summary.period.messages.toLocaleString(),
      icon: MessagesSquare,
    });
  }
  return stats.slice(0, 2);
}
